import DialogContentText from "@material-ui/core/DialogContentText";
import { isSuperUser } from "@saleor/auth/misc";
import { BrandUpdateVariables } from "@saleor/businessdtlsform/types/BrandUpdate";
import ActionDialog from "@saleor/components/ActionDialog";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import { Task } from "@saleor/containers/BackgroundTasks/types";
import { BrandErrorFragment } from "@saleor/fragments/types/BrandErrorFragment";
import useBackgroundTask from "@saleor/hooks/useBackgroundTask";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import { commonMessages } from "@saleor/intl";
import { findValueInEnum, maybe } from "@saleor/misc";
import useBrandGroupSearch from "@saleor/searches/useBrandGroupSearch";
import useClusterSearch from "@saleor/searches/useClusterSearch";
import AssignStatusDialog from "@saleor/seller/components/AssignStatusDialog";
import {
  BrandVerticalEnum,
  PermissionEnum,
  ShopifyErrorCode,
  SmopStatusEnum,
  StoreTypeEnum
} from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import {
  hasNoError,
  updateStatusValidation
} from "@saleor/utils/validators/seller";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import SellerDetailsPage from "../../components/SellerDetailsPage";
import { SellerDetailsPageFormData } from "../../components/SellerDetailsPage";
import ViewStoreDialog from "../../components/ViewStoreDialog";
import {
  useBrandCreateDeeplink,
  useSellerUpdate,
  useSellerUpdateStatus,
  useShopifyBrandSync,
  useShopifyCredentialsCheck,
  useShopifyTagsAdd,
  useshopifyTagsRemove
} from "../../mutations";
import { useSellerDetailsQuery } from "../../queries";
import { brandUpdate } from "../../types/brandUpdate";
import { UpdateBrandStatus } from "../../types/UpdateBrandStatus";
import { sellerDetailsUrl, SellerListPath } from "../../urls";
import { SellerListUrlDialog, SellerListUrlQueryParams } from "../../urls";

interface SellerDetailsProp {
  id: string;
  params: SellerListUrlQueryParams;
}
export const SellerDetails: React.FC<SellerDetailsProp> = ({ id, params }) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();
  const { queue } = useBackgroundTask();

  const { data, loading, refetch } = useSellerDetailsQuery({
    displayLoader: true,
    variables: {
      id
    }
  });

  const {
    user: currentUser,
    user: { userPermissions }
  } = useUser();

  const hasShopifyPermission = userPermissions.some(
    permission => permission.code === PermissionEnum.MANAGE_SHOPIFY
  );

  const searchCluster = useClusterSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 5
    }
  });

  const handleUpdateSuccess = (data: brandUpdate) => {
    if (data.brandUpdate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Seller Updated"
        })
      });
      refetch();
      closeModal();
    } else if (data.brandUpdate.errors.some(e => e.code === "REQUIRED")) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Some Fields have null value !"
        })
      });
    } else if (
      data.brandUpdate.errors.some(e => e.code === "INVALID") &&
      data.brandUpdate.errors.some(e => e.field === "swornDeclarationsFlag")
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Each sworn declaration can only be edited once per year."
        })
      });
    } else if (
      data.brandUpdate.errors.some(e => e.code === "INVALID") &&
      data.brandUpdate.errors.some(e => e.field === "logo")
    ) {
      const imageError = data.brandUpdate.errors.find(
        error => error.field === ("logo" as keyof BrandUpdateVariables)
      );
      if (imageError) {
        notify({
          status: "error",
          text: data.brandUpdate.errors[0].message
        });
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "File not supported"
          })
        });
      }
    } else {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "There's an error!"
        })
      });
    }
  };

  const [sellerUpdate, sellerUpdateResult] = useSellerUpdate({
    onCompleted: handleUpdateSuccess
  });

  const [updateStatuError, setupdateStatuError] = React.useState<
    BrandErrorFragment
  >();

  const handleUpdateStatusSuccess = (data: UpdateBrandStatus) => {
    if (data.updateBrandStatus.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(
          {
            defaultMessage: "Seller {values}"
          },
          {
            values:
              params.assign === "INA"
                ? "Deactivated"
                : params.assign === "ACT"
                ? "Activated"
                : params.assign === "DCL"
                ? "Declined"
                : "Updated"
          }
        )
      });
      refetch();
      setupdateStatuError(undefined);
      closeModal();
    } else if (
      data.updateBrandStatus.errors.some(e => e.field === "Customer code") ||
      data.updateBrandStatus.errors.some(e => e.field === "vendorCode")
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Please complete the internal details / vertical type of the seller"
        })
      });

      refetch();
      setupdateStatuError(undefined);
      closeModal();
    } else {
      setupdateStatuError(data.updateBrandStatus.errors[0]);
    }
  };

  const [sellerUpdateStatus] = useSellerUpdateStatus({
    onCompleted: handleUpdateStatusSuccess
  });

  const [createDeeplink, createDeeplinkOpts] = useBrandCreateDeeplink({
    onCompleted: data => {
      const errors = data?.brandGenerateDynamicLink?.brandErrors;
      if (errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Seller Updated",
            description: "snackbar text"
          })
        });
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Oops, somethin went wrong...",
            description: "snackbar text"
          })
        });
      }
    }
  });

  const [shopifyTagsAdd] = useShopifyTagsAdd({
    onCompleted: data => {
      const errors = data.shopifyBrandTagAdd.shopifyError;
      if (errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Shopify tag added."
          })
        });
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "An error occured in adding shopifytag"
          })
        });
      }
      refetch();
    }
  });

  const [shopifyTagsRemove] = useshopifyTagsRemove({
    onCompleted: data => {
      const errors = data.shopifyBrandTagRemove.shopifyError;
      if (errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Shopify tag removed."
          })
        });
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "An error occured in removing shopifytag"
          })
        });
      }
      refetch();
    }
  });

  const [shopifyBrandSync, shopifyBrandSyncResult] = useShopifyBrandSync({
    onCompleted: data => {
      const errors = data?.shopifyBrandSync?.shopifyError;
      if (errors?.length === 0) {
        notify({
          text: intl.formatMessage({
            defaultMessage: "Shopify data syncing has started."
          }),
          title: intl.formatMessage({
            defaultMessage: "Syncing Shopify"
          })
        });
        queue(Task.SHOPIFY_SYNC, {
          id: data?.shopifyBrandSync?.pid
        });
      } else if (
        errors?.length !== 0 &&
        errors[0].code === ShopifyErrorCode.INVALID
      ) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage:
              "An error occurred during syncing. Invalid seller provided.",
            description: "snackbar text"
          })
        });
      } else if (
        errors?.length !== 0 &&
        errors[0].code === ShopifyErrorCode.GRAPHQL_ERROR
      ) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: `An error occurred during syncing.`,
            description: "snackbar text"
          })
        });
      } else if (
        errors?.length !== 0 &&
        errors[0].code === ShopifyErrorCode.REQUIRED
      ) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: `An error occurred during syncing. Please specify a seller.`,
            description: "snackbar text"
          })
        });
      }
    }
  });

  const [
    shopifyCredentialsCheck,
    shopifyCredentialsCheckResult
  ] = useShopifyCredentialsCheck({
    onCompleted: data => {
      const errors = data?.shopifyCredentialsCheck?.shopifyError;
      const status = data?.shopifyCredentialsCheck?.status;

      if (errors?.length === 0 && status === "Success") {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage:
              "Shopify credentials have been successfully validated.",
            description: "snackbar text"
          })
        });
      } else if (
        (errors?.length !== 0 && errors[0].code === ShopifyErrorCode.INVALID) ||
        status === "Failed"
      ) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage:
              "An error occurred during validation. Invalid credentials provided.",
            description: "snackbar text"
          })
        });
      } else if (
        errors?.length !== 0 &&
        errors[0].code === ShopifyErrorCode.GRAPHQL_ERROR
      ) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: `An error occurred during validation.`,
            description: "snackbar text"
          })
        });
      } else if (
        errors?.length !== 0 &&
        errors[0].code === ShopifyErrorCode.REQUIRED
      ) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: `An error occurred during validation. Please specify the required fields.`,
            description: "snackbar text"
          })
        });
      } else {
        notify({
          status: "error",
          text: intl.formatMessage(commonMessages.somethingWentWrong)
        });
      }
    }
  });

  function handleShopifySync() {
    shopifyBrandSync({
      variables: {
        brand: id
      }
    });
  }

  function handleVerifyShopifyConnection(
    shopifyApiSecret: string,
    shopifyApiShop: string,
    verifyByBrandId?: boolean
  ) {
    shopifyCredentialsCheck({
      variables: {
        input: {
          ...(verifyByBrandId
            ? {
                brand: id
              }
            : {
                shopifyApiSecret,
                shopifyApiShop
              })
        }
      }
    });
  }

  const [openModal, closeModal] = createDialogActionHandlers<
    SellerListUrlDialog,
    SellerListUrlQueryParams
  >(navigate, params => sellerDetailsUrl(id, params), params);

  const updateValidationForm = {
    customerCode: null,
    internalDetails: null,
    vendorCode: null,
    vertical: null
  };
  const [updateStatusError, setupdateStatusError] = useState(
    updateValidationForm
  );
  const handleUpdateStatus = () => {
    const validate = updateStatusValidation(data?.brandPrivate, params.assign);

    if (hasNoError(validate)) {
      sellerUpdateStatus({
        variables: {
          id,
          status: findValueInEnum(params.assign, SmopStatusEnum)
        }
      });
    } else {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Please complete the internal details / vertical type of the seller"
        })
      });
      closeModal();
      setupdateStatusError(validate);
    }
  };

  const getSwornDeclarationsFlag = swornDeclarationsFlag => {
    console.warn("swornDeclarationsFlag", swornDeclarationsFlag);
    return swornDeclarationsFlag !== null || swornDeclarationsFlag !== undefined
      ? swornDeclarationsFlag
      : {};
  };

  const handleSubmit = async (formData: SellerDetailsPageFormData) => {
    const result = await sellerUpdate({
      variables: {
        id,
        input: {
          accountManager: formData.accountManager && formData.accountManager.id,
          bankAccountName: formData.bankAccountName,
          bankAccountNo: formData.bankAccountNo,
          bankName: formData.bankName,
          cluster: formData.cluster,
          code: formData.code,
          completeAddress: formData.completeAddress,
          customerCode: formData.customerCode,
          financeEmail: formData.financeEmail,
          financeMobileNo: formData.financeMobileNo,
          financeName: formData.financeName,
          isLargeTaxpayer: formData.isLargeTaxpayer,
          isPayoutEligible: formData.isPayoutEligible,
          legalName: formData.legalName,
          name: formData.name,
          shopifyApiKey: formData.shopifyApiKey,
          shopifyApiSecret: formData.shopifyApiSecret,
          shopifyApiShop: formData.shopifyApiShop,
          signatoryDesignation: formData.signatoryDesignation,
          signatoryEmail: formData.signatoryEmail,
          signatoryMobileNo: formData.signatoryMobileNo,
          signatoryName: formData.signatoryName,
          status: maybe(() => findValueInEnum(formData.status, SmopStatusEnum)),
          storeType: maybe(() =>
            findValueInEnum(formData.storeType, StoreTypeEnum)
          ),
          ...(isSuperUser(currentUser)
            ? { swornDeclarationsFlag: Boolean(formData.swornDeclarationsFlag) }
            : {
                swornDeclarationsFlag: getSwornDeclarationsFlag(
                  formData.swornDeclarationsFlag
                )
              }),
          tinNo: formData.tinNo,
          vendorCode: formData.vendorCode,
          vertical: getVerticalType(formData)
        }
      }
    });
    setupdateStatusError(updateValidationForm);
    return result.data.brandUpdate.errors;
  };

  function getVerticalType(
    formData: SellerDetailsPageFormData
  ): BrandVerticalEnum {
    if (formData.storeType === StoreTypeEnum.SM_FULFILLED) {
      return BrandVerticalEnum.SM_FULFILLED;
    } else if (formData.storeType === StoreTypeEnum.FOOD_AND_BEVERAGES) {
      return BrandVerticalEnum.FOOD_AND_BEVERAGES;
    } else if (formData.storeType === StoreTypeEnum.GROCERY) {
      return BrandVerticalEnum.GROCERY;
    } else {
      return findValueInEnum(formData.vertical, BrandVerticalEnum);
    }
  }

  const {
    loadMore: loadMoreBrand,
    search: searchBrands,
    result: brandsQueryData
  } = useBrandGroupSearch({
    variables: { ...DEFAULT_INITIAL_SEARCH_DATA, status: "" }
  });
  const sellerBrandChoices = maybe(
    () => brandsQueryData?.data?.search?.edges,
    []
  ).map(edge => edge.node);

  const clusterChoices = maybe(
    () => searchCluster?.result?.data?.search?.edges,
    []
  ).map(edge => edge.node);

  return (
    <>
      <SellerDetailsPage
        hasShopifyPermission={hasShopifyPermission}
        handleShopifySync={handleShopifySync}
        isShopifySyncLoading={shopifyBrandSyncResult.loading}
        isShopifyVerifyCredentialsLoading={
          shopifyCredentialsCheckResult.loading
        }
        handleVerifyShopifyConnection={handleVerifyShopifyConnection}
        seller={data?.brandPrivate}
        disabled={loading || createDeeplinkOpts.loading}
        onBack={() => navigate(SellerListPath)}
        onSubmit={handleSubmit}
        errors={sellerUpdateResult?.data?.brandUpdate.errors || []}
        updateStatusError={updateStatusError}
        saveButtonBarState={sellerUpdateResult.status}
        onAssign={assign => openModal("assign", { assign })}
        onView={() => openModal("view-store")}
        sellerList={sellerBrandChoices}
        clusterList={clusterChoices}
        handleShopifyTags={undefined}
        onFileSizeError={() =>
          notify({
            status: "error",
            text: intl.formatMessage({
              defaultMessage:
                "File size of the image you are trying to upload is too large."
            }),
            title: intl.formatMessage({
              defaultMessage: "Can't Upload."
            })
          })
        }
        onGenerateDeeplink={id => createDeeplink({ variables: { id } })}
        onImageDelete={() => openModal("removeImage")}
        onImageUpload={file =>
          sellerUpdate({
            variables: {
              id,
              input: {
                logo: file
              }
            }
          })
        }
        fetchBrands={query => searchBrands(query)}
        fetchMoreBrands={{
          hasMore: maybe(
            () => brandsQueryData.data.search.pageInfo.hasNextPage
          ),
          loading: brandsQueryData.loading,
          onFetchMore: loadMoreBrand
        }}
        fetchClusters={query => searchCluster.search(query)}
        fetchMoreClusters={{
          hasMore: maybe(
            () => searchCluster.result.data.search.pageInfo.hasNextPage
          ),
          loading: searchCluster.result.loading,
          onFetchMore: searchCluster.loadMore
        }}
        onTagAdd={(tag, type) => {
          shopifyTagsAdd({
            variables: {
              brandId: id,
              kind: type,
              tag
            }
          });
        }}
        onTagRemove={(tag, type) => {
          shopifyTagsRemove({
            variables: {
              brandId: id,
              kind: type,
              tag
            }
          });
        }}
      />

      <ViewStoreDialog
        tenants={data?.brandPrivate?.tenants}
        confirmButtonState={"default"}
        open={params.action === "view-store"}
        onClose={closeModal}
        onConfirm={closeModal}
      />

      <AssignStatusDialog
        confirmButtonState={"default"}
        open={params.action === "assign"}
        onClose={closeModal}
        error={updateStatuError}
        action={params.assign}
        onConfirm={handleUpdateStatus}
      />

      <ActionDialog
        confirmButtonState={sellerUpdateResult.status}
        onClose={closeModal}
        onConfirm={() =>
          sellerUpdate({
            variables: {
              id,
              input: {
                logo: null
              }
            }
          })
        }
        open={params.action === "removeImage"}
        title={intl.formatMessage({
          defaultMessage: "Delete image",
          description: "dialog title"
        })}
        variant="delete"
      >
        <DialogContentText>
          <FormattedMessage defaultMessage="Are you sure you want to delete seller logo?" />
        </DialogContentText>
      </ActionDialog>
    </>
  );
};

export default SellerDetails;
